import React from "react";
import { Link } from "react-router-dom";
import VibeTag from "./VibeTag";
import VideoPoster from "./VideoPoster";

function deriveSignals(p) {
  if (!p || !p.scores) return [];
  const s = p.scores || {};
  const out = [];
  if (s.communication >= 90) out.push("Strong communicator");
  if (s.values >= 90) out.push("Values aligned");
  if (s.effort >= 90) out.push("Intentional");
  if (out.length === 0 && s.chemistry >= 88) out.push("Chemistry forward");
  if (out.length === 0 && s.futureGoals >= 88) out.push("Future aligned");
  return out.slice(0, 2);
}

export default function ProfileCard({
  p,
  size = "md", // sm, md, lg
  state = "idle", // idle | selected | paused | advanced | locked
  onSelect,
  selectable = false,
  showScore = true,
  className = "",
}) {
  const rings = {
    idle: "ring-1 ring-white/5",
    selected: "ring-gold",
    paused:
      "opacity-50 scale-[0.97] blur-[0.5px] saturate-[0.6] ring-1 ring-white/5",
    advanced: "ring-forest glow-gold-soft",
    locked: "ring-1 ring-white/5",
  };
  const signals = deriveSignals(p);
  const videoSrc = p.video_url || p.video || p.intro_video_url || null;
  const aspect =
    size === "sm" ? "aspect-[3/4]" : size === "lg" ? "aspect-[3/4]" : "aspect-[3/4]";
  return (
    <div
      data-testid={`profile-card-${p.id}`}
      className={`group relative overflow-hidden rounded-2xl bg-[#080B14] transition-all duration-300 ${rings[state]} ${aspect} ${className}`}
    >
      <img
        src={p.image || p.photo || p.profile_image || p.avatar || (p.intro_video_url ? `/thumbnails/${p.id}.jpg` : "/logo.png")}
        alt={p.name}
        loading="lazy"
        className="absolute inset-0 h-full w-full object-cover transition-transform duration-700 group-hover:scale-[1.05]"
      />
      {videoSrc && (
        <video
          ref={(el) => { if (el) el.muted = true; }}
          src={videoSrc}
          className="absolute inset-0 w-full h-full object-cover z-10"
          autoPlay
          loop
          playsInline
          preload="auto"
        />
      )}
      <div className="absolute inset-0 bg-gradient-to-t from-[#05070d] via-[#05070d]/55 to-transparent z-20" />
      {/* Top badges */}
      <div className="absolute top-3 left-3 right-3 flex items-center justify-between gap-2 z-30">
        {p.hasVideo && (
          <span className="glass rounded-full px-2 py-1 text-[10px] uppercase tracking-[0.18em] text-[#F2DE94] flex items-center gap-1.5">
            <svg viewBox="0 0 24 24" className="h-3 w-3 fill-current">
              <path d="M8 5v14l11-7L8 5Z" />
            </svg>
            Intro
          </span>
        )}
        {showScore && (
          <span className="glass rounded-full px-2 py-1 text-[11px] font-mono text-white/85 ml-auto">
            {p.compatibility}%
          </span>
        )}
      </div>

      {state === "advanced" && (
        <span className="absolute top-3 right-3 rounded-full bg-[#4AA36A]/90 text-[#05070D] text-[10px] uppercase tracking-[0.2em] font-semibold px-2 py-1 z-30">
          Advanced
        </span>
      )}

      {/* Content */}
      <div className="absolute inset-x-0 bottom-0 p-4 md:p-5 z-30">
        <div className="flex items-baseline gap-2">
          <h3 className="font-display text-2xl leading-tight">
            {p.name}
            <span className="text-white/50 font-body font-normal text-lg ml-1.5">
              {p.age}
            </span>
          </h3>
        </div>
        <div className="text-xs text-white/55 mt-0.5">
          {p.occupation} · {p.city}
        </div>
        <p className="text-sm text-white/75 mt-2 line-clamp-2 italic font-display">
          "{p.headline}"
        </p>
        {signals.length > 0 && (
          <div className="mt-3 flex flex-wrap gap-1.5">
            {signals.map((sig) => (
              <span
                key={sig}
                data-testid="profile-signal"
                className="inline-flex items-center gap-1.5 rounded-full border border-[rgba(212,175,55,0.35)] bg-[rgba(212,175,55,0.08)] text-[#F2DE94] text-[10px] tracking-[0.12em] uppercase px-2 py-0.5"
              >
                <span className="h-1 w-1 rounded-full bg-[#D4AF37]" />
                {sig}
              </span>
            ))}
          </div>
        )}
        <div className="mt-3 flex flex-wrap gap-1.5">
          {(p.tags || []).slice(0, 3).map((t) => (
            <VibeTag key={t} tone="ghost">
              {t}
            </VibeTag>
          ))}
        </div>

        <div className="mt-4 flex items-center gap-2">
          <Link
            to={`/profile/${p.id}`}
            data-testid={`view-profile-${p.id}`}
            className="flex-1 text-center text-xs font-medium text-white/80 rounded-full border border-white/10 hover:border-white/30 hover:bg-white/5 px-3 py-2 transition-colors"
          >
            View profile
          </Link>
          {selectable && (
            <button
              data-testid={`select-prospect-${p.id}`}
              onClick={onSelect}
              className={`text-xs font-semibold rounded-full px-3.5 py-2 transition-all ${
                state === "selected"
                  ? "bg-[#4AA36A] text-[#05070D]"
                  : "bg-gradient-gold text-[#080B14] hover:scale-[1.03]"
              }`}
            >
              {state === "selected" ? "Shortlisted ✓" : "Add to 7"}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
